/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import dayjs from 'dayjs';

import { environment } from 'environments/environment';
import { Constants } from 'app/shared/models/constants.model';

@Injectable()
export class ProfileService {
  constructor(private httpClient: HttpClient) {}

  getUserById(id: any): Observable<any> {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/users/${id}`));
  }

  updateUserById(payload: any, id: number): Observable<any> {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/users/${id}`, payload));
  }

  addUserClub(payload: any): Observable<any> {
    return this.handleResponse(this.httpClient.post(`${environment.apiHostname}/v1/user-club`, payload));
  }

  updateUserClubById(payload: any, id: number): Observable<any> {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/user-club/${id}`, payload));
  }

  deleteUserClubById(id: number): Observable<any> {
    return this.handleResponse(this.httpClient.delete(`${environment.apiHostname}/v1/user-club/${id}`));
  }

  addUserArticle(payload: any): Observable<any> {
    return this.handleResponse(this.httpClient.post(`${environment.apiHostname}/v1/user-article`, payload));
  }

  updateUserArticleById(payload: any, id: number): Observable<any> {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/user-article/${id}`, payload));
  }

  deleteUserArticleById(id: number): Observable<any> {
    return this.handleResponse(this.httpClient.delete(`${environment.apiHostname}/v1/user-article/${id}`));
  }

  recordViewAction(id: number) {
    return this.handleResponse(this.httpClient.post(`${environment.apiHostname}/v1/user-actions/view/user/${id}`, {}));
  }

  getUserByProfileUrl(url: string): Observable<any> {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/users/profile-url/url/${url}`));
  }

  follow(idFollowee: any) {
    return this.httpClient.put(`${environment.apiHostname}/v1/user-actions/follow/${idFollowee}`, {}).pipe(
      mergeMap((response: any) => {
        return this.httpClient.get(`${environment.apiHostname}/v1/users/${idFollowee}`).pipe(
          map((userResponse: any) => {
            if (!response.result.success) {
              throw new Error(response.result.message);
            }
            if (userResponse.result.success) {
              const res = { ...userResponse.data };
              res.isLoggedInUserFollowing = true;
              res.isLoggedInUserFollowingConfirmed = response.data.confirmed;
              return res;
            } else {
              throw new Error(userResponse.result.message);
            }
          })
        );
      })
    );
  }

  unfollow(idFollowee: any) {
    return this.httpClient.put(`${environment.apiHostname}/v1/user-actions/unfollow/${idFollowee}`, {}).pipe(
      mergeMap((response: any) => {
        return this.httpClient.get(`${environment.apiHostname}/v1/users/${idFollowee}`).pipe(
          map((userResponse: any) => {
            if (!response.result.success) {
              throw new Error(response.result.message);
            }
            if (userResponse.result.success) {
              const res = { ...userResponse.data };
              res.isLoggedInUserFollowing = false;
              return res;
            } else {
              throw new Error(userResponse.result.message);
            }
          })
        );
      })
    );
  }

  block(id: any) {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/user-actions/block/${id}`, {}));
  }

  unblock(id: any) {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/user-actions/unblock/${id}`, {}));
  }

  getAthleteAllHighlights(id: any) {
    return this.httpClient.get(`${environment.apiHostname}/v1/highlight-reels/list/highlight-reels/${id}`).pipe(
      map((resp: any) => {
        if (resp.result.success) {
          return resp.data.filter((req: any) => req.asset).map((req: any) => req.asset);
        } else {
          throw new Error(resp.result.message);
        }
      })
    );
  }

  getAthleteHighlights(userData: any) {
    const showPrivateVideos = userData.isLoggedInUser;
    const showPublicVideos = true;

    // get Highlight reels for user and inviters
    return forkJoin([this.getAthleteAllHighlights(userData.id)]).pipe(
      map((hrLists) => {
        // flatten array of results
        return hrLists.reduce((acc, list) => acc.concat(list), []);
      }),
      mergeMap((resp) => {
        return this.getAthleteVisibleAssets(
          userData,
          Constants.ASSET_TYPE.ATHLETE_HIGHLIGHT_VIDEO,
          showPrivateVideos,
          showPublicVideos,
          resp
        );
      })
    );
  }

  getAthleteVisibleAssets(
    userData: any,
    assetType: string,
    showPrivateVideos: boolean,
    showPublicVideos: boolean,
    assets: any[]
  ) {
    return this.getAthletePrivateAssets(userData.id, assetType).pipe(
      map((privateResp: any[]) => {
        const privateAssetIds = privateResp.map((asset) => asset.id);
        assets = assets.filter((asset) =>
          privateAssetIds.indexOf(asset.id) >= 0 ? showPrivateVideos : showPublicVideos
        );
        if (userData.isLoggedInUser) {
          for (const asset of assets) {
            asset.private = privateAssetIds.indexOf(asset.id) >= 0;
          }
        }
        return assets;
      })
    );
  }

  getAthletePrivateAssets(id: number, assetType?: string) {
    return this.httpClient.get(`${environment.apiHostname}/v1/users/${id}/private/assets`).pipe(
      map((resp: any) => {
        if (resp.result.success) {
          const assets: any[] = resp.data.length === 0 ? [] : resp.data[0].privateAssets;
          if (assetType) {
            return assets.filter((asset) => asset.type === assetType);
          } else {
            return assets;
          }
        }
        return [];
      })
    );
  }

  getAthleteEvents(playerUsername: any, playerEmail: any, showFutureEvents: any, year: any) {
    return this.httpClient
      .post(`${environment.apiHostname}/v1/events/search`, {
        playerEmail,
        year,
        showOnlyFutureEvents: showFutureEvents,
      })
      .pipe(
        map((response: any) => {
          if (response.result.success) {
            response.data.forEach((t) => (t.month = dayjs(t.startDate).format('YYYY-MM')));
            return response.data;
          } else {
            throw new Error(response.result.message);
          }
        })
      );
  }

  getAthleteFollowers(id: any) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/user-actions/followers-of/${id}`));
  }

  getAthleteFollowing(id: any) {
    return this.httpClient.get(`${environment.apiHostname}/v1/user-actions/followed-by/${id}`).pipe(
      map((response: any) => {
        if (response.result.success) {
          return {
            athletesFollowing: response.data.filter((t) => t.type === Constants.USER_TYPE.ATHLETE),
            usersFollowing: response.data.filter((t) => t.type === Constants.USER_TYPE.USER),
          };
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }

  setUserCoverImage(userData: any, file: any, fileName: string) {
    const params: any = {
      bucketName: environment.bucketName,
      folderName: environment.webAssetsFolderName.concat('/', 'users'),
    };
    return this.httpClient
      .post(`${environment.apiHostname}/v1/assets`, this.getAssetFormDataFromFile(file, fileName), { params })
      .pipe(
        mergeMap((assetResponse: any) => {
          const userDataToSave = Object.assign({}, userData);
          userDataToSave.password = null;
          userDataToSave.coverImage = { id: assetResponse.data.id };
          return this.httpClient
            .put(`${environment.apiHostname}/v1/users/${userData.id}`, userDataToSave)
            .pipe(map((userUpdateResponse: any) => this.handleSetImageResponse(assetResponse, userUpdateResponse)));
        })
      );
  }

  setUserAvatarImage(userData: any, file: any, fileName: string) {
    const params: any = {
      bucketName: environment.bucketName,
      folderName: environment.webAssetsFolderName.concat('/', 'users'),
    };
    return this.httpClient
      .post(`${environment.apiHostname}/v1/assets`, this.getAssetFormDataFromFile(file, fileName), { params })
      .pipe(
        mergeMap((assetResponse: any) => {
          const userDataToSave = Object.assign({}, userData);
          userDataToSave.password = null;
          userDataToSave.image = { id: assetResponse.data.id };
          return this.httpClient
            .put(`${environment.apiHostname}/v1/users/${userData.id}`, userDataToSave)
            .pipe(map((userUpdateResponse: any) => this.handleSetImageResponse(assetResponse, userUpdateResponse)));
        })
      );
  }

  saveUserFullName(userId: number, fullName: string) {
    return this.httpClient.get(`${environment.apiHostname}/v1/users/${userId}`).pipe(
      mergeMap((responseUser: any) => {
        const userData = { ...responseUser.data };
        const firstNameLastName = fullName.split(' ').filter((namePart) => namePart && namePart !== '');
        userData.lastName = firstNameLastName.length > 0 ? firstNameLastName[firstNameLastName.length - 1] : '';
        userData.firstName = fullName.replace(userData.lastName, '').trim();
        userData.password = null;
        return this.httpClient.put(`${environment.apiHostname}/v1/users/${userId}`, userData).pipe(
          map((resp: any) => {
            return { firstName: resp.data.firstName, lastName: resp.data.lastName };
          })
        );
      })
    );
  }

  saveUserBioAcademicsAthletics(userFormData: any) {
    return this.httpClient.get(`${environment.apiHostname}/v1/users/${userFormData.id}`).pipe(
      mergeMap((responseUser: any) => {
        const userData = { ...responseUser.data };
        const oldId = userData.userDetails.id;
        Object.assign(userData.userDetails, userFormData);
        userData.userDetails.id = oldId;
        userData.password = null;
        return this.httpClient.put(`${environment.apiHostname}/v1/users/${userData.id}`, userData);
      })
    );
  }

  getAthleteHrByAssetId(id: number) {
    return this.httpClient.get(`${environment.apiHostname}/v1/highlight-reels/asset/${id}`).pipe(
      map((resp: any) => {
        if (resp.result.success) {
          return resp.data;
        }
        return null;
      })
    );
  }

  setDraft(payload: any, id: number) {
    return this.handleResponse(this.httpClient.put(`${environment.apiHostname}/v1/users/set-draft/${id}`, payload));
  }

  getProfileAssets(userId: number) {
    return this.handleResponse(this.httpClient.get(`${environment.apiHostname}/v1/users/${userId}/profile/assets`));
  }

  setProfileAssets(userId: number, assetIds: number[]) {
    return this.handleResponse(
      this.httpClient.post(`${environment.apiHostname}/v1/users/set-profile/assets/user/${userId}`, {
        assetIds,
      })
    );
  }

  removeProfileAssets(userId: number, assetIds: number[]) {
    return this.handleResponse(
      this.httpClient.post(`${environment.apiHostname}/v1/users/remove-profile/assets/user/${userId}`, {
        assetIds,
      })
    );
  }

  private handleResponse(methodCallResponse: Observable<object>) {
    return methodCallResponse.pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.data;
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }

  private getAssetFormDataFromFile(file: any, fileName: string) {
    const fd = new FormData();
    fd.append('file', this.converterDataURItoBlob(file), fileName);
    return fd;
  }

  private handleSetImageResponse(assetResponse: any, userUpdateResponse: any) {
    if (userUpdateResponse.result.success) {
      return assetResponse.data;
    } else {
      throw new Error(userUpdateResponse.result.message);
    }
  }

  private converterDataURItoBlob(dataURI) {
    let byteString;
    let mimeString;
    let ia;

    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = encodeURI(dataURI.split(',')[1]);
    }
    // separate out the mime component
    mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to a typed array
    ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], { type: mimeString });
  }

  private isPurchaseForUser(purchase, user) {
    if (!purchase.invites || !purchase.invites.length) {
      return false;
    }

    return purchase.invites.some((invite) => {
      if (!invite.inviteeEmail) {
        return false;
      }
      return invite.inviteeEmail.toLowerCase() === user.email.toLowerCase();
    });
  }

  private getHighlightReelsForPurchases(purchaseIds: number[]): Observable<any> {
    if (!purchaseIds.length) {
      return of([]);
    }

    return this.handleResponse(
      this.httpClient.get(
        `${
          environment.apiHostname
        }/v1/highlight-reels/list/highlight-reels/by/purchase-ids?purchaseIds=${purchaseIds.join(',')}`
      )
    );
  }
}
